import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Flex, Typography } from '@/shared/antd-imports';
import { StarOutlined } from '@ant-design/icons';
import { useGetPendingMilestoneRatingsQuery } from '@/store/api';
import type { PendingMilestoneRating } from '@/types/milestone-ratings';
import { RateDeliveryModal } from './RateDeliveryModal';

/**
 * Spec 15 on the dashboard: deliveries waiting for the client's rating (done in the last 60 days, not rated yet).
 * Renders nothing while loading, on error (the project page still asks) or when there is nothing to rate.
 */
export const PendingRatingsCard: React.FC = () => {
  const { t } = useTranslation();
  const { data } = useGetPendingMilestoneRatingsQuery();
  const [rating, setRating] = useState<PendingMilestoneRating | null>(null);
  const pending = data?.body ?? [];
  if (pending.length === 0) return null;

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <Flex gap={8} align="center">
          <StarOutlined />
          {t('milestoneRatings.pendingTitle', { defaultValue: 'How did we do?' })}
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Typography.Text type="secondary">
          {t('milestoneRatings.pendingHelp', {
            defaultValue: 'These deliveries are done. A quick rating helps us do better.',
          })}
        </Typography.Text>
        {pending.map(item => (
          <Flex key={item.id} align="center" justify="space-between" gap={12} wrap="wrap">
            <Flex vertical gap={0} style={{ minWidth: 0 }}>
              <Typography.Text strong>{item.name}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {item.project_name}
              </Typography.Text>
            </Flex>
            <Button type="primary" size="small" onClick={() => setRating(item)}>
              {t('milestoneRatings.rate', { defaultValue: 'Rate this delivery' })}
            </Button>
          </Flex>
        ))}
      </Flex>
      {rating && <RateDeliveryModal projectId={rating.project_id} milestone={rating} onClose={() => setRating(null)} />}
    </Card>
  );
};
